import { Injectable, signal } from '@angular/core';
import {
    disabled,
    form,
    maxLength,
    required,
    validate,
} from '@angular/forms/signals';
import type { StartBuildingOwnershipTransferRequest } from '@quipex/shared/contracts';
import { emailCheck, type SignalFormState } from '@quipex/shared/utils';
import { defer, EMPTY, of, type Observable } from 'rxjs';

export interface TransferBuildingOwnershipFormValues {
    recipientEmail: string;
    retainOriginalOwnerAccess: boolean;
}

const checkEmail = emailCheck();

@Injectable()
export class FormState implements SignalFormState<
    TransferBuildingOwnershipFormValues,
    StartBuildingOwnershipTransferRequest
> {
    private readonly processing = signal(false);
    public readonly id = 'transfer-building-ownership';
    private readonly model = signal<TransferBuildingOwnershipFormValues>({
        recipientEmail: '',
        retainOriginalOwnerAccess: false,
    });
    public readonly form = form(this.model, (path) => {
        required(path.recipientEmail);
        maxLength(path.recipientEmail, 100);
        validate(path.recipientEmail, ({ value }) =>
            checkEmail(value()) ? { kind: 'email' } : null
        );
        disabled(path.retainOriginalOwnerAccess, () => this.processing());
    });

    public setProcessing(processing: boolean): void {
        this.processing.set(processing);
    }

    public validateAndBuildPayload(): Observable<StartBuildingOwnershipTransferRequest> {
        return defer(() => {
            const form = this.form();
            if (form.invalid() || form.pending()) {
                form.markAsTouched();
                return EMPTY;
            }
            return of(this.buildPayload(this.model()));
        });
    }

    private buildPayload(
        values: TransferBuildingOwnershipFormValues
    ): StartBuildingOwnershipTransferRequest {
        return {
            recipientEmail: values.recipientEmail.trim(),
            retainOriginalOwnerAccess: values.retainOriginalOwnerAccess,
        };
    }
}
